interface ApiClientOptions {
  baseURL?: string
  withCredentials?: boolean
  rejectUnauthorized?: boolean
}

interface RequestConfig {
  headers?: Record<string, string>
  body?: any
  signal?: AbortSignal
  params?: Record<string, any>
  [key: string]: any
}

export class ApiClient {
  private baseURL: string
  private withCredentials: boolean
  private authToken: string | null

  constructor(options: ApiClientOptions) {
    this.baseURL = options.baseURL ?? ''
    this.withCredentials = options.withCredentials || false
    this.authToken = null
  }

  setAuthToken(token: string | null): void {
    this.authToken = token
  }

  getAuthToken(): string | null {
    return this.authToken
  }

  setBaseURL(baseURL: string): void {
    this.baseURL = baseURL
  }

  getBaseURL(): string {
    return this.baseURL
  }

  private getBearerTokenFromCookie(): string | null {
    if (typeof document !== 'undefined') {
      const cookies = document.cookie.split(';').reduce((acc, cookie) => {
        const [key, value] = cookie.trim().split('=')
        if (key && value !== undefined) {
          return { ...acc, [key]: decodeURIComponent(value) }
        }
        return acc
      }, {} as Record<string, string>)

      return cookies.token || null // Get the "token" cookie if it exists, otherwise return null
    }
    else {
      return null
    }
  }

  private redirectToLogin(): void {
    if (typeof window !== 'undefined') {
      console.log('🔄 Token expired - redirecting to public login page')
      window.location.assign('/login')
    }
  }

  private clearAuthCookie(): void {
    if (typeof document !== 'undefined') {
      // Clear the token cookie
      document.cookie = 'token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=' + window.location.hostname
      document.cookie = 'token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/'
    }
  }

  private async sendRequest<T = any>(method: string, url: string, config?: RequestConfig): Promise<T> {
    let fullUrl = url.startsWith('http') ? url : `${this.baseURL}${url}`
    
    // Handle query parameters
    if (config?.params) {
      const params = new URLSearchParams()
      Object.entries(config.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value))
        }
      })
      const queryString = params.toString()
      if (queryString) {
        fullUrl += (fullUrl.includes('?') ? '&' : '?') + queryString
      }
    }
    
    const requestConfig: RequestInit = {
      method: method.toUpperCase(),
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-store, no-cache, must-revalidate, max-age=0',
        'Pragma': 'no-cache',
        'Expires': '0',
        ...config?.headers,
      },
      credentials: this.withCredentials ? 'include' : 'same-origin',
      signal: config?.signal,
    }

    // Handle request body for POST, PUT, PATCH requests
    if (config?.body && (method.toLowerCase() === 'post' || method.toLowerCase() === 'put' || method.toLowerCase() === 'patch')) {
      if (typeof config.body === 'object') {
        requestConfig.body = JSON.stringify(config.body)
      } else {
        requestConfig.body = config.body
      }
    }

    // Check if there is a "token" cookie in the user's browser
    const tokenFromCookie = this.getBearerTokenFromCookie()
    if (tokenFromCookie) {
      requestConfig.headers = {
        ...requestConfig.headers,
        Authorization: `Bearer ${tokenFromCookie}`,
      }
    }

    try {
      const response = await fetch(fullUrl, requestConfig)

      // Check for authentication errors
      if (response.status === 401) {
        console.warn('🔐 Authentication failed - token may be expired')
        this.clearAuthCookie()
        this.redirectToLogin()
        throw new Error('Authentication failed - redirecting to login')
      }

      // Check if the response is ok (status in the range 200-299)
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      // Try to parse as JSON first
      try {
        const data = await response.json()
        
        // Check for database token expiry error in response
        if (data && data.error && typeof data.error === 'string' && 
            data.error.toLowerCase().includes('token has expired')) {
          console.warn('🔐 Token expired error detected in response')
          this.clearAuthCookie()
          this.redirectToLogin()
          throw new Error('Token expired - redirecting to login')
        }
        
        return data
      } catch (parseError) {
        // If JSON parsing fails, return the response text
        const text = await response.text()
        
        // Check for token expiry in text response
        if (text && text.toLowerCase().includes('token has expired')) {
          console.warn('🔐 Token expired error detected in text response')
          this.clearAuthCookie()
          this.redirectToLogin()
          throw new Error('Token expired - redirecting to login')
        }
        
        return text as T
      }
    } catch (error) {
      // Re-throw authentication errors
      if (error instanceof Error && error.message.includes('redirecting to login')) {
        throw error
      }
      throw error
    }
  }

  get<T = any>(url: string, config?: RequestConfig): Promise<T> {
    return this.sendRequest<T>('get', url, config)
  }

  post<T = any>(url: string, data?: any, config?: RequestConfig): Promise<T> {
    return this.sendRequest<T>('post', url, { ...config, body: data })
  }

  put<T = any>(url: string, data?: any, config?: RequestConfig): Promise<T> {
    return this.sendRequest<T>('put', url, { ...config, body: data })
  }

  patch<T = any>(url: string, data?: any, config?: RequestConfig): Promise<T> {
    return this.sendRequest<T>('patch', url, { ...config, body: data })
  }

  delete<T = any>(url: string, config?: RequestConfig): Promise<T> {
    return this.sendRequest<T>('delete', url, config)
  }
}

const api = new ApiClient({})
export type ApiClientInstance = typeof api

export default api
